import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

const TaskStatusSchema = z.enum([
  'draft',
  'pending',
  'queued',
  'in_progress',
  'completed',
]);

/**
 * Schema for tasks which represent units of work in a project
 */
export const TaskSchema = z.object({
  taskId: PrimaryKeySchema,
  projectId: PrimaryKeySchema,
  title: z.string(),
  description: z.string(),
  metadata: z.record(z.string(), z.string()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  status: TaskStatusSchema,
  createdBy: PrimaryKeySchema.nullable(),
  updatedBy: PrimaryKeySchema.nullable(),
});

export type Task = z.infer<typeof TaskSchema>;

export const TasksSchema = z.array(TaskSchema);

export type Tasks = z.infer<typeof TasksSchema>;

export const CreateTaskSchema = TaskSchema.pick({
  projectId: true,
  title: true,
  description: true,
  metadata: true,
}).extend({
  shouldStart: z.boolean().optional(),
});

export type CreateTask = z.infer<typeof CreateTaskSchema>;

export const UpdateTaskSchema = TaskSchema.pick({
  title: true,
  description: true,
  metadata: true,
})
  .partial()
  .extend({
    shouldStart: z.boolean().optional(),
  });

export type UpdateTask = z.infer<typeof UpdateTaskSchema>;

export const TasksQueryOptionsSchema = z
  .object({
    taskIds: commaDelimitedList(z.string().array()).optional(),
    projectIds: commaDelimitedList(z.string().array()).optional(),
    titleLike: z.string().optional(),
    statuses: commaDelimitedList(
      z.enum(TaskStatusSchema.options).array(),
    ).optional(),
  })
  .merge(
    createOffsetPaginationRequestQuerySchema(
      z.enum(['createdAt', 'updatedAt']),
    ),
  );

export type TasksQueryOptions = z.infer<typeof TasksQueryOptionsSchema>;
